import { safeSetDoc } from '../utils';
import { useState, useEffect } from 'react';
import { doc } from 'firebase/firestore';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { db, auth } from '../firebase';
import { appId } from '../constants';
import Avatar from './Avatar';
import { Users, Plus, Key } from 'lucide-react';

const LocalUserSwitcher = ({ onLoading }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        let usersList = [];

        if (import.meta.env.DEV) {
          const { default: authExport } = await import('../emulator-data/auth_export/accounts.json');
          usersList = (authExport.users || []).map((u) => ({
            uid: u.localId,
            name: u.displayName || 'Unnamed User',
            email: u.email,
            avatar: u.photoUrl || null,
          }));
        }

        setUsers(usersList);
      } catch (err) {
        console.error('Error loading local users:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleSwitchUser = async (user) => {
    onLoading(true);
    try {
      // In Firebase Auth Emulator, any password works if the user exists.
      // We'll use a dummy password 'password' for all local accounts.
      await signInWithEmailAndPassword(auth, user.email || `${user.uid}@local.test`, 'password');
    } catch (err) {
      console.error('Error switching user:', err);
      alert('Failed to switch user. Auth credentials may be out of sync. Please run the recovery script.');
    } finally {
      onLoading(false);
    }
  };

  const handleCreateIdentity = async () => {
    const name = prompt('Enter name for new local identity:');
    if (!name) return;
    
    onLoading(true);
    const dummyEmail = `${name.toLowerCase().replace(/\s+/g, '.')}-${Date.now()}@local.test`;
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, dummyEmail, 'password');
      await updateProfile(userCredential.user, { displayName: name });
      
      // Fix race condition: AuthContext might have already provisioned them as 'User'
      await safeSetDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'users', userCredential.user.uid),
        { name: name },
        { merge: true }
      );
      
      window.location.reload();
    } catch (err) {
      console.error('Error creating identity:', err);
      alert('Failed to create identity.');
    } finally {
      onLoading(false);
    }
  };

  if (loading) return null;

  return (
    <div className="mt-8 pt-8 border-t border-zinc-100 w-full max-w-md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
          <Users size={12} /> Local Identities
        </h3>
        <button
          onClick={handleCreateIdentity}
          className="p-1 hover:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-900 transition-colors"
          title="Create New Identity"
        >
          <Plus size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2">
        {users.length === 0 ? (
          <p className="text-[10px] text-zinc-400 italic">No local users found.</p>
        ) : (
          users.map((u) => (
            <button
              key={u.uid}
              onClick={() => handleSwitchUser(u)}
              className="w-full flex items-center gap-3 p-3 bg-zinc-50 hover:bg-zinc-100 rounded-2xl border border-zinc-100 transition-all active:scale-[0.98] group"
            >
              <Avatar src={u.avatar} size={32} className="rounded-lg shadow-sm" />
              <div className="flex flex-col items-start min-w-0 flex-1">
                <span className="text-sm font-bold text-zinc-800 truncate">{u.name}</span>
                <span className="text-[9px] text-zinc-400 font-medium truncate uppercase tracking-tight">
                  {u.email || 'No Email'}
                </span>
              </div>
              <Key size={14} className="text-zinc-200 group-hover:text-zinc-400 transition-colors" />
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default LocalUserSwitcher;
